import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '../../../../incase-domain/components/button/Button';
import {FolderChip} from './FolderChip';
import {FOLDER_FILTER_ALL, FOLDER_FILTER_UNFILED, IPresentationFolder} from './types';

export const FOLDER_DROPPABLE_PREFIX = 'folder:';
export const PRESENTATION_LIST_DROPPABLE_ID = 'presentation-list';

interface FolderNavProps {
    folders: IPresentationFolder[];
    selectedFolder: string;
    onSelect: (folder: string) => void;
    onNewFolder: () => void;
    onRenameFolder: (folder: IPresentationFolder) => void;
    onDeleteFolder: (folder: IPresentationFolder) => void;
    loading?: boolean;
    unfiledCount?: number;
    cursorFolderKey?: string | null;
}

function FolderNav(props: FolderNavProps): JSX.Element {
    const {folders, selectedFolder, onSelect, onNewFolder, onRenameFolder, onDeleteFolder, loading, unfiledCount, cursorFolderKey} = props;
    const {t} = useTranslation();
    const scrollRowRef = useRef<HTMLDivElement>(null);
    const suppressClickRef = useRef(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const showScrollArrows = canScrollLeft || canScrollRight;

    const updateScrollArrows = useCallback((): void => {
        const row = scrollRowRef.current;
        if (!row) return;
        const overflows = row.scrollWidth - row.clientWidth > 4;
        setCanScrollLeft(overflows && row.scrollLeft > 4);
        setCanScrollRight(overflows && row.scrollLeft + row.clientWidth < row.scrollWidth - 4);
    }, []);

    useEffect(() => {
        const row = scrollRowRef.current;
        updateScrollArrows();
        if (!row) return undefined;
        const observer = new ResizeObserver(() => updateScrollArrows());
        observer.observe(row);
        return () => observer.disconnect();
    }, [folders, loading, showScrollArrows, updateScrollArrows]);

    const scrollFolders = (direction: 1 | -1): void => {
        scrollRowRef.current?.scrollBy({left: direction * 240, behavior: 'smooth'});
    };

    const handleScrollPointerDown = (event: React.PointerEvent): void => {
        if (event.button !== 0 || event.pointerType === 'touch') return;
        const row = scrollRowRef.current;
        if (!row) return;
        const startX = event.clientX;
        const startScroll = row.scrollLeft;
        let moved = false;
        const handlePointerMove = (moveEvent: PointerEvent): void => {
            const deltaX = moveEvent.clientX - startX;
            if (!moved && Math.abs(deltaX) > 6) {
                moved = true;
                row.classList.add('is-swiping');
                document.body.dispatchEvent(new MouseEvent('mousedown', {bubbles: true}));
            }
            if (moved) {
                row.scrollLeft = startScroll - deltaX;
            }
        };
        const handlePointerUp = (): void => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
            row.classList.remove('is-swiping');
            if (moved) {
                suppressClickRef.current = true;
                window.setTimeout(() => {
                    suppressClickRef.current = false;
                }, 0);
            }
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };

    const handleScrollClickCapture = (event: React.MouseEvent): void => {
        if (suppressClickRef.current) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    return (
        <div className="c-folderNav" data-cy="presentation-folders">
            <div className="c-folderNav__row">
                <FolderChip
                    folderKey={FOLDER_FILTER_ALL}
                    droppableId={`${FOLDER_DROPPABLE_PREFIX}${FOLDER_FILTER_ALL}`}
                    selected={selectedFolder === FOLDER_FILTER_ALL}
                    icon="solar:layers-linear"
                    title={t('editorPage.folders.allPresentations')}
                    onSelect={() => onSelect(FOLDER_FILTER_ALL)}
                    dataCy="folder-all"
                    highlighted={cursorFolderKey === FOLDER_FILTER_ALL}
                />
                <FolderChip
                    folderKey={FOLDER_FILTER_UNFILED}
                    droppableId={`${FOLDER_DROPPABLE_PREFIX}${FOLDER_FILTER_UNFILED}`}
                    selected={selectedFolder === FOLDER_FILTER_UNFILED}
                    icon="solar:file-linear"
                    title={t('editorPage.folders.unfiled')}
                    count={unfiledCount}
                    onSelect={() => onSelect(FOLDER_FILTER_UNFILED)}
                    dataCy="folder-unfiled"
                    highlighted={cursorFolderKey === FOLDER_FILTER_UNFILED}
                />
                <div style={{flexGrow: 1}} />
                <Button leading="ph:plus-bold" variant="soft" color="primary" size="xs" onClick={onNewFolder} dataCy="new-folder-button">
                    {t('editorPage.folders.newFolder')}
                </Button>
            </div>
            {(loading || folders.length > 0) && (
                <div className="c-folderNav__scrollWrap">
                    {showScrollArrows && (
                        <Button
                            leading="solar:alt-arrow-left-linear"
                            variant="soft"
                            color="primary"
                            size="xs"
                            square
                            type="button"
                            onClick={() => scrollFolders(-1)}
                            dataCy="folders-scroll-left"
                            disabled={!canScrollLeft}
                            className="c-folderNav__scrollBtn"
                        />
                    )}
                    <div
                        className="c-folderNav__row c-folderNav__row--scroll"
                        ref={scrollRowRef}
                        onPointerDown={handleScrollPointerDown}
                        onClickCapture={handleScrollClickCapture}
                        onScroll={updateScrollArrows}
                    >
                        {folders.map((folder) => (
                            <FolderChip
                                key={folder._id}
                                folderKey={folder._id}
                                droppableId={`${FOLDER_DROPPABLE_PREFIX}${folder._id}`}
                                selected={selectedFolder === folder._id}
                                icon="solar:folder-linear"
                                title={folder.name}
                                count={folder.presentationCount}
                                onSelect={() => onSelect(folder._id)}
                                dataCy={`folder-${folder._id}`}
                                highlighted={cursorFolderKey === folder._id}
                                actionItems={[
                                    [
                                        {
                                            icon: 'solar:pen-2-linear',
                                            label: t('editorPage.folders.rename'),
                                            onClick: () => onRenameFolder(folder),
                                            dataCy: 'rename-folder',
                                        },
                                        {
                                            icon: 'solar:trash-bin-minimalistic-linear',
                                            label: t('editorPage.folders.delete'),
                                            onClick: () => onDeleteFolder(folder),
                                            color: 'error',
                                            dataCy: 'delete-folder',
                                        },
                                    ],
                                ]}
                            />
                        ))}
                    </div>
                    {showScrollArrows && (
                        <Button
                            leading="solar:alt-arrow-right-linear"
                            variant="soft"
                            color="primary"
                            size="xs"
                            square
                            type="button"
                            onClick={() => scrollFolders(1)}
                            dataCy="folders-scroll-right"
                            disabled={!canScrollRight}
                            className="c-folderNav__scrollBtn"
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default FolderNav;
